import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSession } from "../../auth/session"
import { EntitlementOrigin, entitlementOriginLabels, License, LicenseType, licenseTypeLabels, LibraryEntry, Product, Sku } from "../../api/models"
import { useLibrary } from "../../redux/librarySlice"
import { FetchableStateStatus } from "../../redux/fetchableState"
import Spinner from "../widgets/Spinner"
import CardImage from "../widgets/CardImage"
import SkuName from "../widgets/SkuName"
import ProductLinks from "../widgets/ProductLinks"
import ChipSelect from "../widgets/ChipSelect"
import { buildImageSrcSet } from "../widgets/imageSrcSet"
import LockCountdown from "../widgets/LockCountdown"
import { codeIcon, freeIcon, grantIcon, ilokIconWhite, ilokLinkedIcon, oneTimeIcon, rewardIcon, subscriptionIcon } from "../widgets/pillIcons"
import Api from "../../api/api"
import IlokDepositDialog from "../IlokDepositDialog"
import TransferEntitlementDialog from "../TransferEntitlementDialog"
import { RENT_TO_OWN_ENABLED } from "../../featureFlags"

// Everything the reverse iLok direction needs for button visibility: the
// user's outbound deposits, the skus with an unambiguous reverse mapping,
// and the confirmed linked iLok accounts. null until loaded (buttons hidden).
type IlokDepositState = {
  deposits: { entitlementId: string, ilokId: string, ilokLicenseGuid: string | null, revokedAt: string | null }[],
  // inbound: which linked account each ilok-origin entitlement came through
  bindings: { entitlementId: string, ilokId: string, revokedAt: string | null }[],
  depositableSkuIds: string[],
  linkedIlokIds: string[],
}

// The ownership-first account view: one card per thing the user acquired
// (an active entitlement — purchase, subscription, code, grant, reward, free
// claim, or an alpha/beta/dev test deposit), carrying the sku's installers
// and the entitled products with their licenses. Trials are product-level
// demo licenses outside entitlements and live on the Dashboard, not here.

// canonical order for the origin filter — like the type filter, the dropdown
// only offers the origins the user actually has, and hides below two
const ORIGIN_FILTER_ORDER: EntitlementOrigin[] = [
  EntitlementOrigin.Order,
  EntitlementOrigin.Subscription,
  EntitlementOrigin.Code,
  EntitlementOrigin.Reward,
  EntitlementOrigin.Grant,
  EntitlementOrigin.FreeClaim,
  EntitlementOrigin.Ilok,
  EntitlementOrigin.Reseller
]

const originIcons: Record<EntitlementOrigin, ReactNode> = {
  [EntitlementOrigin.Order]: oneTimeIcon,
  [EntitlementOrigin.Subscription]: subscriptionIcon,
  [EntitlementOrigin.Code]: codeIcon,
  [EntitlementOrigin.Reward]: rewardIcon,
  [EntitlementOrigin.Grant]: grantIcon,
  [EntitlementOrigin.FreeClaim]: freeIcon,
  // the origin pill gets the chain-link glyph (issued through the linked
  // account) — the iLok MARK is the bind indicator in the card header (both
  // directions), not an origin icon, and the key means "a license"
  [EntitlementOrigin.Ilok]: ilokLinkedIcon,
  // a reseller code is a voucher like our own codes
  [EntitlementOrigin.Reseller]: codeIcon
}

// the Library speaks the storefront's language — the plan is branded
// Rent-to-Own for users (admin surfaces keep the technical "Subscription")
const libraryOriginLabels: Record<EntitlementOrigin, string> = {
  ...entitlementOriginLabels,
  [EntitlementOrigin.Subscription]: "Rent-to-Own"
}

// canonical order for the type filter — the dropdown only offers the types
// the user actually owns, and hides entirely when there's just one (most
// users hold nothing but standard). Demo is trial-only; trials live on the
// Dashboard.
const TYPE_FILTER_ORDER: LicenseType[] = [
  LicenseType.Standard,
  LicenseType.Beta,
  LicenseType.NFR,
  LicenseType.Free,
  LicenseType.Alpha,
  LicenseType.Dev
]

export function isPast(date: string | null | undefined): boolean {
  return date != null && new Date(date) < new Date()
}

// Subscriptions renew rather than expire — a past renews date means billing
// lag, not a lapsed entitlement — so they never count as expired. Shared
// with the Installers page so both hide the same entitlements by default.
export function entryExpired(entry: LibraryEntry): boolean {
  return entry.origin !== EntitlementOrigin.Subscription && isPast(entry.expiresAt)
}

function OriginLink({ entry }: { entry: LibraryEntry }) {
  const inner = <>{originIcons[entry.origin]}{libraryOriginLabels[entry.origin]}</>
  // handed over from another account: the ref names THEIR order/redemption,
  // which we can't open — say where it came from instead of linking
  if (entry.transferredAt != null)
    return (
      <span className="library-origin" title={`Transferred to you on ${new Date(entry.transferredAt).toLocaleDateString()}`}>
        {originIcons[entry.origin]}Transferred
      </span>
    )
  if (entry.origin === EntitlementOrigin.Order)
    return <Link to={`/orders/${entry.refId}`} className="card-link library-origin">{inner}</Link>
  if (entry.origin === EntitlementOrigin.Code)
    return <Link to={`/redemptions/${entry.refId}`} className="card-link library-origin">{inner}</Link>
  if (entry.origin === EntitlementOrigin.Subscription && RENT_TO_OWN_ENABLED)
    return <Link to="/plan" className="card-link library-origin">{inner}</Link>
  return <span className="library-origin">{inner}</span>
}

// 18px product thumbnail (or placeholder) for the product filter's chip and
// dropdown rows — ProductFilter's sizing
function productChipIcon(product: Product): ReactNode {
  if (product.imageUrl)
    return <img src={product.imageUrl} srcSet={buildImageSrcSet(product.imageUrl, product.availableWidths)} sizes="18px" alt="" style={{ width: 18, height: 18, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
  return (
    <span className="library-product-icon library-product-icon-placeholder" style={{ width: 18, height: 18 }} aria-hidden="true">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    </span>
  )
}

function ProductIcon({ product }: { product?: Product }) {
  if (product?.imageUrl)
    return <img src={product.imageUrl} srcSet={buildImageSrcSet(product.imageUrl, product.availableWidths)} sizes="24px" alt="" className="library-product-icon" />
  return (
    <span className="library-product-icon library-product-icon-placeholder" aria-hidden="true">
      {/* feather "image" */}
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    </span>
  )
}

function ProductCell({ product }: { product?: Product }) {
  return (
    <span className="library-product">
      <ProductIcon product={product} />
      {product?.name ?? "unknown product"}
    </span>
  )
}

function activationsText(license: License) {
  return `${license.activationIds.active.length} / ${license.maxActivations}`
}

// One product row of an entry: the product plus its license (rows repeat for
// the rare product with several licenses on the same entry; a product without
// a license — a deposit that hasn't happened — shows as such). Shared with
// the Redemptions cards, which show a code's deposited licenses the same way.
export interface ProductRow {
  product?: Product
  license?: License
}

function rowsFor(entry: LibraryEntry, productsById: Map<string, Product>, licensesById: Map<string, License>): ProductRow[] {
  const licenses = entry.licenseIds.map(id => licensesById.get(id)).filter((it): it is License => it != null)
  return entry.productIds.flatMap(productId => {
    const product = productsById.get(productId)
    const own = licenses.filter(license => license.productId === productId)
    return own.length > 0 ? own.map(license => ({ product, license })) : [{ product }]
  })
}

// Just product + activations — type, expiry AND the upgrade lock are
// entitlement-level, all shown once in the card header (an upgrade checkout
// reserves whole entitlements, so per-license Locked badges would only
// repeat it). A row with a license opens its page; the chevron at the row's
// end is always visible so the affordance doesn't depend on hovering.
export function LicenseRows({ rows }: { rows: ProductRow[] }) {
  const navigate = useNavigate()
  const open = (license?: License) => license && navigate(`/licenses/${license.id}`)
  return (
    <>
      <div className="mobile-list">
        {rows.map((row, i) => (
          <div key={row.license?.id ?? `${row.product?.id}-${i}`} className={`mobile-list-item${row.license ? " mobile-list-item-opens" : " mobile-list-item-static"}`} onClick={() => open(row.license)}>
            <div className="mobile-list-kv">
              <span className="mobile-list-kv-label">Product</span>
              <span className="mobile-list-kv-value"><ProductCell product={row.product} /></span>
            </div>
            {row.license ? (
              <div className="mobile-list-kv">
                <span className="mobile-list-kv-label">Activations</span>
                <span className="mobile-list-kv-value">{activationsText(row.license)}</span>
              </div>
            ) : (
              <div className="mobile-list-kv">
                <span className="mobile-list-kv-label">License</span>
                <span className="mobile-list-kv-value library-muted">not deposited</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="table-scroll mobile-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Activations</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.license?.id ?? `${row.product?.id}-${i}`} className={row.license ? "table-row-clickable" : undefined} onClick={() => open(row.license)}>
                <td><ProductCell product={row.product} /></td>
                {row.license ? (
                  <td>{activationsText(row.license)}</td>
                ) : (
                  <td className="library-muted">no license deposited</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// The entitlement's expiry, worded by its nature: subscriptions renew, the
// rest expire; a permanent entitlement shows nothing.
function EntryExpiry({ entry }: { entry: LibraryEntry }) {
  if (!entry.expiresAt) return null
  const renews = entry.origin === EntitlementOrigin.Subscription
  const expired = !renews && isPast(entry.expiresAt)
  const className = expired ? "library-expiry-past" : renews ? undefined : "library-expiry-upcoming"
  return (
    <>
      <span className="library-meta-sep">·</span>
      <span className={className}>
        {renews ? "Renews" : expired ? "Expired" : "Expires"} {new Date(entry.expiresAt).toLocaleDateString()}
      </span>
    </>
  )
}

function EntryCard({ entry, sku, rows, accessToken, ilokDeposits, onIlokDeposited, onLibraryChanged }: { entry: LibraryEntry, sku?: Sku, rows: ProductRow[], accessToken: string, ilokDeposits: IlokDepositState | null, onIlokDeposited: () => void, onLibraryChanged: () => void }) {
  // the entitlement's license type — all its licenses carry it; no badge for
  // standard (the normal case), same way no expiry means permanent.
  // alpha/beta go orange: pre-release builds should draw the eye
  const typeBadge = entry.licenseType === LicenseType.Standard ? undefined : licenseTypeLabels[entry.licenseType]
  const attention = entry.licenseType === LicenseType.Alpha || entry.licenseType === LicenseType.Beta
  // reserved as an upgrade checkout's trade-in — the lock is
  // entitlement-level, so it shows once here, not on every license row
  const locked = entry.lockedUntil !== undefined && new Date(entry.lockedUntil) > new Date()
  const name = sku?.name ?? entry.skuId
  const [ilokDepositOpen, setIlokDepositOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  // handing the entitlement to another account — the server's call
  // (transferable on the entry; absent on an older backend = no action). A
  // still-refundable order is the one block worth explaining: it opens on a
  // known date. Every other block (type, origin, lock, expiry) is already
  // visible on the card or self-evident.
  const canTransfer = entry.transferable === true
  const transferableFrom = entry.transferBlock === "refund_window" && entry.transferableFrom != null
    ? new Date(entry.transferableFrom) : null
  // reverse iLok direction: perpetual, standard, NFR or beta (mirrors the backend's
  // ILOK_DEPOSITABLE_LICENSE_TYPES), not itself iLok-derived or
  // subscription-backed, a curated iLok deposit target, an account to target, and
  // not already deposited (library entries are active-only)
  // all response arrays are optional-chained: a deployed backend older than
  // this build may omit newer fields, and the card must render regardless
  const ilokDeposit = ilokDeposits?.deposits?.find(d => d.entitlementId === entry.id)
  const ilokBinding = ilokDeposits?.bindings?.find(b => b.entitlementId === entry.id)
  // iLok-BOUND is the state of having a LIVE iLok counterpart in either
  // direction: issued from an iLok license (origin) that hasn't been revoked
  // since (an upgrade consumed the entitlement and a refund brought it back
  // natively only), or deposited out to one. A missing binding row (older
  // backend) keeps the ilok-origin mark, as before.
  const ilokBound = (entry.origin === EntitlementOrigin.Ilok && (ilokBinding == null || ilokBinding.revokedAt == null))
    || (ilokDeposit != null && ilokDeposit.ilokLicenseGuid !== null && ilokDeposit.revokedAt === null)
  // the mark's tooltip names the counterpart account: inbound = the linked
  // account the ilok-origin entitlement was issued through, outbound = the
  // account the deposit went to
  const ilokBindTitle = entry.origin === EntitlementOrigin.Ilok
    ? (ilokBinding != null ? `iLok-bound (${ilokBinding.ilokId})` : "iLok-bound")
    : (ilokDeposit != null ? `iLok-bound (${ilokDeposit.ilokId})` : "iLok-bound")
  // the entitlement's own side of the rule (mirrors ilok_eligibility.ts) —
  // a perpetual standard/NFR/beta right that isn't iLok-derived or
  // subscription-backed and has no deposit
  const ilokDepositableRight = ilokDeposit === undefined
    && (entry.licenseType === LicenseType.Standard || entry.licenseType === LicenseType.NFR || entry.licenseType === LicenseType.Beta)
    && entry.expiresAt === null
    && entry.origin !== EntitlementOrigin.Ilok
    && entry.origin !== EntitlementOrigin.Subscription
  // issued iLok-disabled (grant / code batch): the link's slot carries a
  // note instead, on every such right — whether or not the sku has an iLok
  // deposit target or the user a linked account (2026-09-23). Absent on an
  // older backend = enabled.
  const ilokWithheld = ilokDepositableRight && entry.ilokEnabled === false
  const canDepositIlok = ilokDepositableRight
    && entry.ilokEnabled !== false
    && ilokDeposits !== null
    && (ilokDeposits.linkedIlokIds?.length ?? 0) > 0
    && (ilokDeposits.depositableSkuIds?.includes(entry.skuId) ?? false)
  return (
    <div className={`license-group${sku?.imageUrl ? " license-group-with-img" : ""}`}>
      {sku?.imageUrl && (
        <div className="license-group-img-container">
          <CardImage imageUrl={sku.imageUrl} availableWidths={sku.availableWidths} alt={name} />
        </div>
      )}
      <div className="product-group-header">
        {sku?.imageUrl && (
          <img src={sku.imageUrl} srcSet={buildImageSrcSet(sku.imageUrl, sku.availableWidths)} sizes="44px" alt="" className="product-header-icon" />
        )}
        <h2 className="section-title library-entry-title">
          {sku ? <SkuName sku={sku} variant="section" /> : name}
          {typeBadge && <span className={`badge-stage${attention ? " badge-stage-attention" : ""}`}>{typeBadge}</span>}
          {locked && <span className="badge-locked">Locked</span>}
          {locked && <LockCountdown lockedUntil={entry.lockedUntil!} />}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* the iLok chip takes the "Get iLok license" slot: once the
              entitlement has its iLok counterpart the link is gone and the
              mark stands where it was (2026-09-23). The two never show
              together — bound excludes depositable. */}
          {ilokBound && (
            <span className="library-ilok-bind" title={ilokBindTitle}>
              {ilokIconWhite}
            </span>
          )}
          {ilokWithheld && (
            <span
              className="library-muted"
              style={{ fontSize: "10pt", cursor: "help" }}
              title="This license does not allow iLok counterpart to be issued"
            >
              iLok not enabled
            </span>
          )}
          {canDepositIlok && (
            <button
              className="card-link"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
              onClick={() => setIlokDepositOpen(true)}
            >
              Get iLok license
            </button>
          )}
          {canTransfer && (
            <button
              className="card-link"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
              onClick={() => setTransferOpen(true)}
            >
              Transfer
            </button>
          )}
          {transferableFrom !== null && (
            <span
              className="library-muted"
              style={{ fontSize: "10pt", cursor: "help" }}
              title="A license can be transferred to another account once its order's 14-day refund window has closed"
            >
              Transferable from {transferableFrom.toLocaleDateString()}
            </span>
          )}
          {/* a card for the sku's test version offers only its test build (the
              released installer isn't what this entitlement is for — it stays
              reachable via All versions); released-version cards conversely
              hide the test-build link the shared sku object carries */}
          {sku && <ProductLinks
            skus={[sku]}
            showProductPage={false}
            allVersionsAccessToken={accessToken}
            show={sku.testVersionId != null && entry.skuVersionId === sku.testVersionId ? "test" : "released"}
          />}
        </div>
      </div>
      <div className="library-entry-body">
        <div className="library-entry-meta">
          <OriginLink entry={entry} />
          <span className="library-meta-sep">·</span>
          {new Date(entry.createdAt).toLocaleDateString()}
          <EntryExpiry entry={entry} />
        </div>
        {rows.length > 0
          ? <LicenseRows rows={rows} />
          : <p className="empty-state library-muted">This version doesn't include any licensed products.</p>}
      </div>
      {ilokDepositOpen && ilokDeposits !== null && (
        <IlokDepositDialog
          entitlementId={entry.id}
          skuName={name}
          linkedIlokIds={ilokDeposits.linkedIlokIds}
          accessToken={accessToken}
          onClose={() => setIlokDepositOpen(false)}
          onDone={onIlokDeposited}
          onRefreshAccounts={onIlokDeposited}
          onAccountsChanged={() => {
            onIlokDeposited()
            // a completed link may have auto-issued entitlements
            onLibraryChanged()
          }}
        />
      )}
      {transferOpen && (
        <TransferEntitlementDialog
          entitlementId={entry.id}
          skuName={name}
          skuVersion={entry.skuVersion}
          ilokBound={ilokDeposit != null && ilokDeposit.ilokLicenseGuid !== null && ilokDeposit.revokedAt === null}
          accessToken={accessToken}
          onClose={() => setTransferOpen(false)}
        />
      )}
    </div>
  )
}

function EmptyLibrary() {
  return (
    <div className="empty-state-block">
      <span className="empty-state-icon">&#128218;</span>
      <span>You don't have any licenses yet</span>
      <span className="empty-state-hint">Get your first product from the store or redeem a code</span>
      <div style={{ display: "flex", gap: 24, marginTop: 12 }}>
        <Link to="/store" className="card-link">Visit Store</Link>
        <Link to="/redemptions" className="card-link">Redeem Code</Link>
      </div>
    </div>
  )
}

export default function Library() {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: library, status, refresh: refreshLibrary } = useLibrary(accessToken)
  // the reverse iLok direction's state — fetch failure just hides the
  // affordance (null), never breaks the Library
  const [ilokDeposits, setIlokDeposits] = useState<IlokDepositState | null>(null)
  const loadIlokDeposits = useCallback(() => {
    Api.getIlokDeposits({ accessToken })
      .then(setIlokDeposits)
      .catch(() => {})
  }, [accessToken])
  useEffect(() => {
    if (!accessToken) return
    loadIlokDeposits()
  }, [accessToken, loadIlokDeposits])
  const [productFilter, setProductFilter] = useState("")
  const [originFilter, setOriginFilter] = useState<"" | EntitlementOrigin>("")
  const [typeFilter, setTypeFilter] = useState<"" | LicenseType>("")
  const [showExpired, setShowExpired] = useState(false)

  // computed from ALL entries (not the filtered view) so the options don't
  // shrink while other filters are active
  const { ownedProducts, ownedOrigins, ownedTypes, expiredCount } = useMemo(() => {
    const entries = library?.entries ?? []
    const entitledIds = new Set(entries.flatMap(entry => entry.productIds))
    return {
      ownedProducts: (library?.products ?? [])
        .filter(product => entitledIds.has(product.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
      ownedOrigins: ORIGIN_FILTER_ORDER.filter(origin => entries.some(entry => entry.origin === origin)),
      ownedTypes: TYPE_FILTER_ORDER.filter(type => entries.some(entry => entry.licenseType === type)),
      expiredCount: entries.filter(entryExpired).length
    }
  }, [library])
  // with a dropdown hidden, a previously picked value must not keep
  // filtering invisibly
  const effectiveProductFilter = ownedProducts.length > 1 ? productFilter : ""
  const effectiveOriginFilter = ownedOrigins.length > 1 ? originFilter : ""
  const effectiveTypeFilter = ownedTypes.length > 1 ? typeFilter : ""

  const view = useMemo(() => {
    if (!library) return null
    const productsById = new Map(library.products.map(product => [product.id, product]))
    const skusById = new Map(library.skus.map(sku => [sku.id, sku]))
    const licensesById = new Map(library.licenses.map(license => [license.id, license]))
    return library.entries
      .filter(entry => (effectiveProductFilter === "" || entry.productIds.includes(effectiveProductFilter))
        && (effectiveOriginFilter === "" || entry.origin === effectiveOriginFilter)
        && (effectiveTypeFilter === "" || entry.licenseType === effectiveTypeFilter)
        && (showExpired || !entryExpired(entry)))
      .map(entry => ({
        entry,
        sku: skusById.get(entry.skuId),
        rows: rowsFor(entry, productsById, licensesById)
      }))
  }, [library, effectiveProductFilter, effectiveOriginFilter, effectiveTypeFilter, showExpired])

  const entryCount = library?.entries.length ?? 0
  const isEmpty = library != null && entryCount === 0
  const noMatches = view !== null && !isEmpty && view.length === 0

  return (
    <div className="page">
      <h1 className="page-title">Licenses</h1>

      {view !== null && !isEmpty && (ownedProducts.length > 1 || ownedOrigins.length > 1 || ownedTypes.length > 1 || expiredCount > 0) && (
        <div className="filter-bar">
          {ownedProducts.length > 1 && (
            <ChipSelect
              value={productFilter}
              onChange={setProductFilter}
              options={ownedProducts.map(product => product.id)}
              labels={Object.fromEntries(ownedProducts.map(product => [product.id, product.name]))}
              icons={Object.fromEntries(ownedProducts.map(product => [product.id, productChipIcon(product)]))}
              placeholder="All products"
            />
          )}
          {ownedOrigins.length > 1 && (
            <ChipSelect
              value={originFilter}
              onChange={setOriginFilter}
              options={ownedOrigins}
              labels={libraryOriginLabels}
              icons={originIcons}
              placeholder="All origins"
            />
          )}
          {ownedTypes.length > 1 && (
            <ChipSelect
              value={typeFilter}
              onChange={setTypeFilter}
              options={ownedTypes}
              labels={licenseTypeLabels}
              placeholder="All license types"
            />
          )}
          {expiredCount > 0 && (
            <button
              className={`skus-filter-chip icon-chip${showExpired ? " skus-filter-chip-active" : ""}`}
              onClick={() => setShowExpired(prev => !prev)}
            >
              {showExpired ? `Showing expired (${expiredCount}) ✕` : `Show expired (${expiredCount})`}
            </button>
          )}
        </div>
      )}

      {view === null && status !== FetchableStateStatus.Error && <p className="empty-state"><Spinner size={16} /> Loading licenses…</p>}
      {view === null && status === FetchableStateStatus.Error && <p className="empty-state">Error loading licenses</p>}
      {isEmpty && <EmptyLibrary />}
      {noMatches && <p className="empty-state">Nothing here matches the current filters.</p>}

      {view?.map(({ entry, sku, rows }) => (
        <EntryCard key={entry.id} entry={entry} sku={sku} rows={rows} accessToken={accessToken}
          ilokDeposits={ilokDeposits} onIlokDeposited={loadIlokDeposits} onLibraryChanged={refreshLibrary} />
      ))}
    </div>
  )
}
